"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import type { UserProfile } from "@/lib/users/types";
import type { OrgMembership } from "@/lib/organizations/types";
import { useAuthSession } from "@/lib/auth/hooks/useAuthSession";
import { useAuthProfile } from "@/lib/auth/hooks/useAuthProfile";
import { useAuthAdmin } from "@/lib/auth/hooks/useAuthAdmin";

/**
 * Optional contact info to pre-fill in the LoginModal.
 * Passed from the chatbot when the user clicks "Kostenfrei registrieren".
 */
export interface LoginModalPrefill {
  email?: string;
  phone?: string;
}

/**
 * Impersonation state stored in sessionStorage
 */
interface ImpersonationState {
  targetUserId: string;
  targetUserEmail: string;
  targetUserName: string;
  adminEmail: string;
  adminAccessToken: string;
  adminRefreshToken: string;
  startedAt: string;
}

/**
 * Activity counts for conditional navigation display
 */
export interface ActivityCounts {
  offerings: number;
  requests: number;
}

/**
 * Auth context type
 */
interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAnonymous: boolean;
  signOut: () => Promise<void>;
  openLoginModal: (prefill?: LoginModalPrefill) => void;
  closeLoginModal: () => void;
  isLoginModalOpen: boolean;
  loginModalPrefill: LoginModalPrefill | null;
  openProfileModal: () => void;
  closeProfileModal: () => void;
  isProfileModalOpen: boolean;
  // Profile
  userProfile: UserProfile | null;
  isProfileCheckDone: boolean;
  refetchProfile: () => Promise<void>;
  // Activity counts for conditional nav
  activityCounts: ActivityCounts | null;
  // Organization
  organization: OrgMembership | null;
  // Impersonation
  isImpersonating: boolean;
  impersonatedUser: ImpersonationState | null;
  startImpersonation: (targetUser: {
    id: string;
    email: string;
    name: string;
  }, redirectTo?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

/**
 * Create context with default values
 */
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  isAnonymous: false,
  signOut: async () => {},
  openLoginModal: () => {},
  closeLoginModal: () => {},
  isLoginModalOpen: false,
  loginModalPrefill: null,
  openProfileModal: () => {},
  closeProfileModal: () => {},
  isProfileModalOpen: false,
  // Profile defaults
  userProfile: null,
  isProfileCheckDone: false,
  refetchProfile: async () => {},
  // Activity counts
  activityCounts: null,
  // Organization defaults
  organization: null,
  // Impersonation defaults
  isImpersonating: false,
  impersonatedUser: null,
  startImpersonation: async () => {},
  stopImpersonation: async () => {}
});

/**
 * Props for AuthProvider
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes auth state available to all children.
 *
 * Internally composes three focused hooks:
 * - useAuthSession  — Supabase session lifecycle (init, listener, sign-out)
 * - useAuthAdmin    — Admin status + impersonation
 * - useAuthProfile  — Profile, activity counts, organization membership
 */
export function AuthProvider(t0) {
  const $ = _c(31);
  const {
    children
  } = t0;
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalPrefill, setLoginModalPrefill] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const sessionHook = useAuthSession({
    onSignIn: user => {
      adminHook.checkAndSetAdmin(user.id);
      adminHook.restoreImpersonation(user);
    },
    onSignOut: () => {
      adminHook.resetAdmin();
      profileHook.resetProfile();
    }
  });
  const adminHook = useAuthAdmin(sessionHook.user, sessionHook.supabase);
  const profileHook = useAuthProfile(sessionHook.user, sessionHook.isLoading);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = prefill => {
      setLoginModalPrefill(prefill ?? null);
      setIsLoginModalOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openLoginModal = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsLoginModalOpen(false);
      setLoginModalPrefill(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const closeLoginModal = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsProfileModalOpen(true);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const openProfileModal = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsProfileModalOpen(false);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeProfileModal = t4;
  let t5;
  if ($[4] !== adminHook || $[5] !== profileHook || $[6] !== sessionHook) {
    t5 = async () => {
      adminHook.resetAdmin();
      profileHook.resetProfile();
      await sessionHook.signOut();
    };
    $[4] = adminHook;
    $[5] = profileHook;
    $[6] = sessionHook;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  adminHook;
  profileHook;
  const signOut = t5;
  const t6 = !!sessionHook.user;
  let t7;
  if ($[8] !== adminHook.impersonatedUser || $[9] !== adminHook.isAdmin || $[10] !== adminHook.isImpersonating || $[11] !== adminHook.startImpersonation || $[12] !== adminHook.stopImpersonation || $[13] !== isLoginModalOpen || $[14] !== isProfileModalOpen || $[15] !== loginModalPrefill || $[16] !== profileHook.activityCounts || $[17] !== profileHook.isProfileCheckDone || $[18] !== profileHook.organization || $[19] !== profileHook.refetchProfile || $[20] !== profileHook.userProfile || $[21] !== sessionHook.isAnonymous || $[22] !== sessionHook.isLoading || $[23] !== sessionHook.session || $[24] !== sessionHook.user || $[25] !== signOut || $[26] !== t6) {
    t7 = {
      user: sessionHook.user,
      session: sessionHook.session,
      isLoading: sessionHook.isLoading,
      isAuthenticated: t6,
      isAdmin: adminHook.isAdmin,
      isAnonymous: sessionHook.isAnonymous,
      signOut,
      openLoginModal,
      closeLoginModal,
      isLoginModalOpen,
      loginModalPrefill,
      openProfileModal,
      closeProfileModal,
      isProfileModalOpen,
      userProfile: profileHook.userProfile,
      isProfileCheckDone: profileHook.isProfileCheckDone,
      refetchProfile: profileHook.refetchProfile,
      activityCounts: profileHook.activityCounts,
      organization: profileHook.organization,
      isImpersonating: adminHook.isImpersonating,
      impersonatedUser: adminHook.impersonatedUser,
      startImpersonation: adminHook.startImpersonation,
      stopImpersonation: adminHook.stopImpersonation
    };
    $[8] = adminHook.impersonatedUser;
    $[9] = adminHook.isAdmin;
    $[10] = adminHook.isImpersonating;
    $[11] = adminHook.startImpersonation;
    $[12] = adminHook.stopImpersonation;
    $[13] = isLoginModalOpen;
    $[14] = isProfileModalOpen;
    $[15] = loginModalPrefill;
    $[16] = profileHook.activityCounts;
    $[17] = profileHook.isProfileCheckDone;
    $[18] = profileHook.organization;
    $[19] = profileHook.refetchProfile;
    $[20] = profileHook.userProfile;
    $[21] = sessionHook.isAnonymous;
    $[22] = sessionHook.isLoading;
    $[23] = sessionHook.session;
    $[24] = sessionHook.user;
    $[25] = signOut;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const value = t7;
  let t8;
  if ($[28] !== children || $[29] !== value) {
    t8 = <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
    $[28] = children;
    $[29] = value;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}

/**
 * Hook to access auth state
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Hook to require authentication
 * Returns true if user needs to log in
 */
export function useRequireAuth() {
  const $ = _c(4);
  const {
    isAuthenticated,
    isLoading,
    openLoginModal
  } = useAuth();
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== isLoading || $[2] !== openLoginModal) {
    t0 = {
      isAuthenticated,
      isLoading,
      openLoginModal
    };
    $[0] = isAuthenticated;
    $[1] = isLoading;
    $[2] = openLoginModal;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}